<script lang="ts">
  import type { Api, User } from "../../lib/api";
  import { ApiError } from "../../lib/api";
  import UserCreateForm from "./UserCreateForm.svelte";
  import UserSection from "./UserSection.svelte";
  import Spinner from "../../lib/ui/Spinner.svelte";
  import Banner from "../../lib/ui/Banner.svelte";
  import { dialogs } from "@price-check/ui";

  // One self-contained manager for a single account role. The Users page mounts
  // role="portal"; the Organizations page mounts role="app" — so app users are
  // managed where they live (alongside orgs), not under Users.
  let { api, role }: { api: Api; role: "portal" | "app" } = $props();

  const copy = $derived(
    role === "portal"
      ? {
          createTitle: "Add a portal admin",
          createSubtitle: "Full access to this owner console. Leave the password blank to email an invite.",
          sectionTitle: "Portal admins",
          sectionSubtitle: "Sign into this owner console.",
          tone: "indigo" as const,
        }
      : {
          createTitle: "Add an app user",
          createSubtitle: "Signs into the desktop app. Add them to an organization above. Leave the password blank to email an invite.",
          sectionTitle: "App users",
          sectionSubtitle: "Use the desktop app; add them to organizations above.",
          tone: "slate" as const,
        },
  );

  let users = $state<User[]>([]);
  let loading = $state(true);
  let error = $state("");
  let busyEmail = $state("");

  const shown = $derived(
    role === "portal"
      ? users.filter((u) => u.role === "portal")
      : users.filter((u) => u.role !== "portal"),
  );

  async function load() {
    loading = true;
    error = "";
    try {
      users = await api.listUsers();
    } catch (e) {
      error = e instanceof Error ? e.message : "Failed to load users.";
    } finally {
      loading = false;
    }
  }
  $effect(() => {
    void load();
  });

  // Run a per-user action, then refresh. ApiError messages surface inline.
  async function act(email: string, fn: () => Promise<void>) {
    error = "";
    busyEmail = email;
    try {
      await fn();
      await load();
    } catch (e) {
      error = e instanceof ApiError ? `${email}: ${e.message}` : `${email}: action failed.`;
    } finally {
      busyEmail = "";
    }
  }

  async function onReset(email: string) {
    const pw = (
      await dialogs.prompt({
        title: "Reset password",
        message: `New password for ${email} (min 8 chars):`,
        inputType: "password",
        confirmLabel: "Reset",
      })
    )?.trim();
    if (!pw) return;
    void act(email, () => api.resetUserPassword(email, pw));
  }
  function onInvite(email: string) {
    void act(email, () => api.inviteUser(email));
  }
  async function onDelete(email: string) {
    if (
      !(await dialogs.confirm({
        title: "Delete user",
        message: `Delete ${email}? This removes their login account.`,
        confirmLabel: "Delete",
        tone: "danger",
      }))
    )
      return;
    void act(email, () => api.deleteUser(email));
  }
</script>

<div class="flex flex-col gap-5">
  <UserCreateForm
    {api}
    fixedRole={role}
    title={copy.createTitle}
    subtitle={copy.createSubtitle}
    onCreated={load}
  />

  {#if error}<Banner tone="danger">{error}</Banner>{/if}

  {#if loading}
    <Spinner label="Loading users…" />
  {:else}
    <UserSection
      title={copy.sectionTitle}
      subtitle={copy.sectionSubtitle}
      users={shown}
      tone={copy.tone}
      {busyEmail}
      {onReset}
      {onInvite}
      {onDelete}
    />
  {/if}
</div>
